import { localDateString } from '../../../../utils/helpers/localDate';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
    getFlatTransferPreview,
    executeFlatTransfer
} from '../../../../redux/actions/SocietyPortal/FlatTransferAction/FlatTransferAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_FLAT_TRANSFER_HISTORY
} from '../../../../Routing/routes';

const TRANSFER_REASONS = [
    { value: 'SALE', label: 'Sale' },
    { value: 'GIFT', label: 'Gift' },
    { value: 'INHERITANCE', label: 'Inheritance' },
    { value: 'RELEASE_DEED', label: 'Release Deed' },
    { value: 'OTHER', label: 'Other' }
];

const SETTLEMENT_MODES = [
    { value: 'CLEARANCE', label: 'Clearance', hint: 'Old owner has paid off all dues before transfer.' },
    { value: 'CARRY_FORWARD', label: 'Carry Forward', hint: 'New owner inherits the full outstanding balance.' },
    { value: 'PARTIAL', label: 'Partial', hint: 'Split the outstanding between cleared and carried portions.' }
];

const fmtMoney = (n) => {
    const v = Number(n || 0);
    return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Step = ({ index, label, current }) => (
    <div className="tw-flex tw-items-center tw-gap-2">
        <div className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-semibold ${
            current >= index ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-200 tw-text-gray-500'
        }`}>{index}</div>
        <span className={`tw-text-sm ${current >= index ? 'tw-text-gray-800 tw-font-medium' : 'tw-text-gray-500'}`}>{label}</span>
    </div>
);

const FlatTransferWizard = () => {
    const { flatId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { preview, execute } = useSelector(state => state.FlatTransferReducer);
    const previewData = preview?.data;

    const [currentStep, setCurrentStep] = useState(1);
    const [errors, setErrors] = useState({});

    // Step 1 — new owner
    const [newEntity, setNewEntity] = useState({
        entity_type: 'I',
        first_name: '',
        last_name: '',
        company_name: '',
        phone: '',
        email: '',
        pan_no: '',
        address: ''
    });

    // Step 2 — settlement + fee
    const [transferDate, setTransferDate] = useState(localDateString());
    const [transferReason, setTransferReason] = useState('SALE');
    const [saleConsideration, setSaleConsideration] = useState('');
    const [transferFeeAmount, setTransferFeeAmount] = useState('');
    const [settlementMode, setSettlementMode] = useState('CLEARANCE');
    const [carriedPrincipal, setCarriedPrincipal] = useState('');
    const [carriedInterest, setCarriedInterest] = useState('');
    const [carriedTax, setCarriedTax] = useState('');
    const [carriedAdvance, setCarriedAdvance] = useState('');
    const [clearedAmount, setClearedAmount] = useState('');

    // Step 3 — share cert + remarks
    const [scShares, setScShares] = useState('');
    const [scValue, setScValue] = useState('');
    const [scDistFrom, setScDistFrom] = useState('');
    const [scDistTo, setScDistTo] = useState('');
    const [scRemarks, setScRemarks] = useState('');
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (flatId) {
            dispatch(getFlatTransferPreview({ flatId, society_id: societyId }));
        }
    }, [dispatch, flatId, societyId]);

    // Prefill share cert defaults from active cert when preview loads
    useEffect(() => {
        if (previewData?.active_certificate) {
            setScShares(previewData.active_certificate.total_shares ?? 10);
            setScValue(previewData.active_certificate.share_value ?? 50);
        } else if (previewData) {
            setScShares(10);
            setScValue(50);
        }
    }, [previewData]);

    const netOutstanding = useMemo(
        () => Number(previewData?.portal_balance?.net_outstanding || 0),
        [previewData]
    );

    const partialSum = useMemo(() => (
        Number(carriedPrincipal || 0) +
        Number(carriedInterest || 0) +
        Number(carriedTax || 0) +
        Number(carriedAdvance || 0) +
        Number(clearedAmount || 0)
    ), [carriedPrincipal, carriedInterest, carriedTax, carriedAdvance, clearedAmount]);

    const partialDiff = useMemo(() => +(partialSum - netOutstanding).toFixed(2), [partialSum, netOutstanding]);

    const validateStep1 = () => {
        const e = {};
        if (newEntity.entity_type === 'I' && !newEntity.first_name.trim()) e.first_name = 'First name is required';
        if (newEntity.entity_type === 'C' && !newEntity.company_name.trim()) e.company_name = 'Company name is required';
        if (!newEntity.phone.trim()) e.phone = 'Phone is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const validateStep2 = () => {
        const e = {};
        if (!transferDate) e.transferDate = 'Transfer date is required';
        if (settlementMode === 'PARTIAL') {
            if (Math.abs(partialDiff) > 0.01) {
                e.partial = `Split must equal ₹${fmtMoney(netOutstanding)} (current diff: ₹${fmtMoney(partialDiff)})`;
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleNext = () => {
        if (currentStep === 1) {
            if (validateStep1()) setCurrentStep(2);
            return;
        }
        if (currentStep === 2) {
            if (validateStep2()) setCurrentStep(3);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const handleSubmit = () => {
        const payload = {
            flatId,
            society_id: societyId,
            transfer_date: transferDate,
            transfer_reason: transferReason,
            balance_settlement_mode: settlementMode,
            new_entity: newEntity.entity_type === 'I'
                ? {
                    entity_type: 'I',
                    first_name: newEntity.first_name,
                    last_name: newEntity.last_name,
                    phone: newEntity.phone,
                    email: newEntity.email || undefined,
                    pan_no: newEntity.pan_no || undefined,
                    address: newEntity.address || undefined
                }
                : {
                    entity_type: 'C',
                    company_name: newEntity.company_name,
                    phone: newEntity.phone,
                    email: newEntity.email || undefined,
                    pan_no: newEntity.pan_no || undefined,
                    address: newEntity.address || undefined
                },
            remarks: remarks || undefined
        };

        if (saleConsideration) payload.sale_consideration = Number(saleConsideration);

        const feeAmt = Number(transferFeeAmount || 0);
        if (feeAmt > 0) {
            payload.transfer_fee_amount = feeAmt;
        }

        if (settlementMode === 'PARTIAL') {
            payload.carried_principal = Number(carriedPrincipal || 0);
            payload.carried_interest = Number(carriedInterest || 0);
            payload.carried_tax = Number(carriedTax || 0);
            payload.carried_advance = Number(carriedAdvance || 0);
            payload.cleared_amount = Number(clearedAmount || 0);
        }

        if (scShares || scValue || scDistFrom || scDistTo || scRemarks) {
            payload.share_certificate = {
                total_shares: scShares ? Number(scShares) : undefined,
                share_value: scValue ? Number(scValue) : undefined,
                distinctive_numbers_from: scDistFrom ? Number(scDistFrom) : undefined,
                distinctive_numbers_to: scDistTo ? Number(scDistTo) : undefined,
                remarks: scRemarks || undefined
            };
        }

        payload.onSuccess = () => {
            const historyPath = SOCIETY_PORTAL_FLAT_TRANSFER_HISTORY.replace(':flatId', flatId);
            navigate(`${SOCIETY_PORTAL}${historyPath}`);
        };

        dispatch(executeFlatTransfer(payload));
    };

    if (preview?.loading) {
        return (
            <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600" />
            </div>
        );
    }

    if (preview?.error || !previewData) {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-text-center tw-shadow-sm tw-border tw-border-gray-100">
                <Icon icon="mdi:alert-circle-outline" className="tw-text-4xl tw-text-red-500 tw-mb-2" />
                <h3 className="tw-text-base tw-font-semibold tw-text-gray-800">Unable to load transfer preview</h3>
                <p className="tw-text-sm tw-text-gray-500 tw-mt-1">
                    {preview?.error?.message || 'Please verify the flat and try again.'}
                </p>
                <button
                    onClick={() => navigate(-1)}
                    className="tw-mt-4 tw-px-4 tw-py-1.5 tw-text-sm tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                >
                    Go back
                </button>
            </div>
        );
    }

    const { flat, current_owner, portal_balance, gl_account, reconciliation, active_certificate } = previewData;
    const reconciled = reconciliation?.reconciled;

    return (
        <div className="tw-space-y-4">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button onClick={() => navigate(-1)} className="tw-p-1.5 tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                        <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Transfer Ownership</h1>
                        <p className="tw-text-xs tw-text-gray-500">
                            {flat?.wing?.title} • Flat {flat?.flat_number}
                        </p>
                    </div>
                </div>
            </div>

            {/* Steps indicator */}
            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-4 tw-justify-center">
                    <Step index={1} label="Buyer" current={currentStep} />
                    <div className="tw-w-10 tw-h-px tw-bg-gray-200" />
                    <Step index={2} label="Settle" current={currentStep} />
                    <div className="tw-w-10 tw-h-px tw-bg-gray-200" />
                    <Step index={3} label="Confirm" current={currentStep} />
                </div>
            </div>

            {/* Current owner card — always visible */}
            <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-2">Current Owner</h3>
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-text-sm">
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Name</div>
                        <div className="tw-text-gray-800">{current_owner?.name}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Phone</div>
                        <div className="tw-text-gray-800">{current_owner?.phone}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Start Date</div>
                        <div className="tw-text-gray-800">{current_owner?.start_date}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Share</div>
                        <div className="tw-text-gray-800">{current_owner?.ownership_share}%</div>
                    </div>
                </div>
            </div>

            {/* Step 1 — Buyer */}
            {currentStep === 1 && (
                <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 tw-space-y-3">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">New Owner Details</h3>

                    <div className="tw-flex tw-gap-3">
                        <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm">
                            <input
                                type="radio"
                                checked={newEntity.entity_type === 'I'}
                                onChange={() => setNewEntity({ ...newEntity, entity_type: 'I' })}
                            />
                            Individual
                        </label>
                        <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm">
                            <input
                                type="radio"
                                checked={newEntity.entity_type === 'C'}
                                onChange={() => setNewEntity({ ...newEntity, entity_type: 'C' })}
                            />
                            Company
                        </label>
                    </div>

                    {newEntity.entity_type === 'I' ? (
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">First name *</label>
                                <input
                                    value={newEntity.first_name}
                                    onChange={(e) => setNewEntity({ ...newEntity, first_name: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                                {errors.first_name && <p className="tw-text-xs tw-text-red-500 tw-mt-0.5">{errors.first_name}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Last name</label>
                                <input
                                    value={newEntity.last_name}
                                    onChange={(e) => setNewEntity({ ...newEntity, last_name: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="tw-text-xs tw-text-gray-600">Company name *</label>
                            <input
                                value={newEntity.company_name}
                                onChange={(e) => setNewEntity({ ...newEntity, company_name: e.target.value })}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                            />
                            {errors.company_name && <p className="tw-text-xs tw-text-red-500 tw-mt-0.5">{errors.company_name}</p>}
                        </div>
                    )}

                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                        <div>
                            <label className="tw-text-xs tw-text-gray-600">Phone *</label>
                            <input
                                value={newEntity.phone}
                                onChange={(e) => setNewEntity({ ...newEntity, phone: e.target.value })}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                            />
                            {errors.phone && <p className="tw-text-xs tw-text-red-500 tw-mt-0.5">{errors.phone}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-text-gray-600">Email</label>
                            <input
                                type="email"
                                value={newEntity.email}
                                onChange={(e) => setNewEntity({ ...newEntity, email: e.target.value })}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                            />
                        </div>
                        <div>
                            <label className="tw-text-xs tw-text-gray-600">PAN</label>
                            <input
                                value={newEntity.pan_no}
                                onChange={(e) => setNewEntity({ ...newEntity, pan_no: e.target.value.toUpperCase() })}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                            />
                        </div>
                        <div>
                            <label className="tw-text-xs tw-text-gray-600">Address</label>
                            <input
                                value={newEntity.address}
                                onChange={(e) => setNewEntity({ ...newEntity, address: e.target.value })}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Step 2 — Settle */}
            {currentStep === 2 && (
                <div className="tw-space-y-3">
                    {/* Balance & reconciliation */}
                    <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Balances</h3>
                            <span className={`tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded ${
                                reconciled
                                    ? 'tw-bg-green-100 tw-text-green-700'
                                    : 'tw-bg-red-100 tw-text-red-700'
                            }`}>
                                {reconciled ? 'Reconciled' : `Diff ₹${fmtMoney(reconciliation?.diff)}`}
                            </span>
                        </div>
                        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-text-sm">
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Principal</div>
                                <div className="tw-text-gray-800">₹{fmtMoney(portal_balance?.principal)}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Interest</div>
                                <div className="tw-text-gray-800">₹{fmtMoney(portal_balance?.interest)}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Tax</div>
                                <div className="tw-text-gray-800">₹{fmtMoney(portal_balance?.tax)}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Advance</div>
                                <div className="tw-text-gray-800">₹{fmtMoney(portal_balance?.advance)}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Net outstanding</div>
                                <div className="tw-text-gray-800 tw-font-semibold">₹{fmtMoney(portal_balance?.net_outstanding)}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">GL balance</div>
                                <div className="tw-text-gray-800">₹{fmtMoney(gl_account?.balance)}</div>
                            </div>
                            <div className="tw-col-span-2">
                                <div className="tw-text-xs tw-text-gray-500">GL account</div>
                                <div className="tw-text-gray-800 tw-text-xs">{gl_account?.ac_code} — {gl_account?.name}</div>
                            </div>
                        </div>
                    </div>

                    {/* Transfer meta */}
                    <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Transfer Details</h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3 tw-text-sm">
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Transfer date *</label>
                                <input
                                    type="date"
                                    value={transferDate}
                                    onChange={(e) => setTransferDate(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                                {errors.transferDate && <p className="tw-text-xs tw-text-red-500 tw-mt-0.5">{errors.transferDate}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Reason</label>
                                <select
                                    value={transferReason}
                                    onChange={(e) => setTransferReason(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white"
                                >
                                    {TRANSFER_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Sale consideration (records only)</label>
                                <input
                                    type="number"
                                    value={saleConsideration}
                                    onChange={(e) => setSaleConsideration(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Settlement mode */}
                    <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Balance Settlement</h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                            {SETTLEMENT_MODES.map(m => (
                                <label key={m.value} className={`tw-flex tw-flex-col tw-gap-1 tw-p-3 tw-border tw-rounded-md tw-cursor-pointer ${
                                    settlementMode === m.value
                                        ? 'tw-border-blue-500 tw-bg-blue-50'
                                        : 'tw-border-gray-200 hover:tw-bg-gray-50'
                                }`}>
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <input
                                            type="radio"
                                            checked={settlementMode === m.value}
                                            onChange={() => setSettlementMode(m.value)}
                                        />
                                        <span className="tw-text-sm tw-font-medium tw-text-gray-800">{m.label}</span>
                                    </div>
                                    <p className="tw-text-xs tw-text-gray-500">{m.hint}</p>
                                </label>
                            ))}
                        </div>

                        {settlementMode === 'PARTIAL' && (
                            <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                                <p className="tw-text-xs tw-text-gray-500 tw-mb-2">
                                    Sum must equal ₹{fmtMoney(netOutstanding)} (current: ₹{fmtMoney(partialSum)}, diff: ₹{fmtMoney(partialDiff)})
                                </p>
                                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-5 tw-gap-2 tw-text-sm">
                                    {[
                                        ['Carried principal', carriedPrincipal, setCarriedPrincipal],
                                        ['Carried interest', carriedInterest, setCarriedInterest],
                                        ['Carried tax', carriedTax, setCarriedTax],
                                        ['Carried advance', carriedAdvance, setCarriedAdvance],
                                        ['Cleared amount', clearedAmount, setClearedAmount]
                                    ].map(([label, val, setter], i) => (
                                        <div key={i}>
                                            <label className="tw-text-[11px] tw-text-gray-600">{label}</label>
                                            <input
                                                type="number"
                                                value={val}
                                                onChange={(e) => setter(e.target.value)}
                                                className="tw-w-full tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                            />
                                        </div>
                                    ))}
                                </div>
                                {errors.partial && <p className="tw-text-xs tw-text-red-500 tw-mt-2">{errors.partial}</p>}
                            </div>
                        )}
                    </div>

                    {/* Transfer fee — record only */}
                    <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Transfer Fee (for record only)</h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3 tw-text-sm">
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Amount</label>
                                <input
                                    type="number"
                                    value={transferFeeAmount}
                                    onChange={(e) => setTransferFeeAmount(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                        </div>
                        <p className="tw-text-xs tw-text-gray-500 tw-mt-2 tw-flex tw-items-start tw-gap-1.5">
                            <Icon icon="mdi:information-outline" className="tw-text-gray-400 tw-mt-0.5" />
                            <span>
                                Fee is captured for record only. Collect via a separate receipt — it will not be
                                added to the new owner's ledger or auto-invoiced.
                            </span>
                        </p>
                    </div>
                </div>
            )}

            {/* Step 3 — Confirm */}
            {currentStep === 3 && (
                <div className="tw-space-y-3">
                    {/* Share certificate config */}
                    <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Share Certificate</h3>
                        {active_certificate && (
                            <p className="tw-text-xs tw-text-gray-500 tw-mb-2">
                                Previous cert: {active_certificate.certificate_number} • {active_certificate.total_shares} shares × ₹{fmtMoney(active_certificate.share_value)}
                            </p>
                        )}
                        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-text-sm">
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Total shares</label>
                                <input
                                    type="number"
                                    value={scShares}
                                    onChange={(e) => setScShares(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Share value</label>
                                <input
                                    type="number"
                                    value={scValue}
                                    onChange={(e) => setScValue(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Distinctive from</label>
                                <input
                                    type="number"
                                    value={scDistFrom}
                                    onChange={(e) => setScDistFrom(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Distinctive to</label>
                                <input
                                    type="number"
                                    value={scDistTo}
                                    onChange={(e) => setScDistTo(e.target.value)}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                        </div>
                        <div className="tw-mt-3">
                            <label className="tw-text-xs tw-text-gray-600">Certificate remarks</label>
                            <input
                                value={scRemarks}
                                onChange={(e) => setScRemarks(e.target.value)}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                            />
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Summary</h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-text-sm">
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">From</div>
                                <div className="tw-text-gray-800">{current_owner?.name}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">To</div>
                                <div className="tw-text-gray-800">
                                    {newEntity.entity_type === 'I'
                                        ? `${newEntity.first_name} ${newEntity.last_name}`.trim()
                                        : newEntity.company_name}
                                </div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Transfer date</div>
                                <div className="tw-text-gray-800">{transferDate}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Settlement</div>
                                <div className="tw-text-gray-800">{settlementMode}</div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Fee (for record)</div>
                                <div className="tw-text-gray-800">
                                    {Number(transferFeeAmount || 0) > 0
                                        ? `₹${fmtMoney(transferFeeAmount)}`
                                        : 'None'}
                                </div>
                            </div>
                            <div>
                                <div className="tw-text-xs tw-text-gray-500">Net outstanding</div>
                                <div className="tw-text-gray-800">₹{fmtMoney(netOutstanding)}</div>
                            </div>
                        </div>
                        <div className="tw-mt-3">
                            <label className="tw-text-xs tw-text-gray-600">Final remarks</label>
                            <textarea
                                rows={3}
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Footer actions */}
            <div className="tw-flex tw-justify-between tw-items-center tw-bg-white tw-rounded-lg tw-p-3 tw-shadow-sm tw-border tw-border-gray-100">
                <button
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="tw-px-4 tw-py-1.5 tw-text-sm tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50"
                >
                    Back
                </button>
                {currentStep < 3 ? (
                    <button
                        onClick={handleNext}
                        className="tw-px-4 tw-py-1.5 tw-text-sm tw-rounded-md tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700"
                    >
                        Continue
                    </button>
                ) : (
                    <button
                        onClick={handleSubmit}
                        disabled={execute?.loading}
                        className="tw-px-4 tw-py-1.5 tw-text-sm tw-rounded-md tw-bg-emerald-600 tw-text-white hover:tw-bg-emerald-700 disabled:tw-opacity-60"
                    >
                        {execute?.loading ? 'Posting…' : 'Post Transfer'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default FlatTransferWizard;
